import {Component, Injector} from "@angular/core";
import {LocalizeRouterService} from "@gilsdav/ngx-translate-router";
import {BehaviorSubject} from "rxjs";
import {SideSheetRef} from "@app/presentation/services/side-sheet/side-sheet-ref";
import {LocalizedRouter} from "@app/presentation/services/localized-router";
import {TranslateService} from "@ngx-translate/core";
import {NavigationEnd, Router} from "@angular/router";
import {filter, take} from "rxjs/operators";
import {SplashScreen} from "@capacitor/splash-screen";

interface LanguageSelectorOption {
    code: string;
    label: string;
}

@Component({
    selector: 'app-language-selector',
    templateUrl: './language-selector.component.html',
    styleUrls: ['./language-selector.component.scss']
})
export class LanguageSelectorComponent {

    readonly languages: LanguageSelectorOption[] = [
        {code: 'es', label: this.translateService.instant('language_selector.spanish')},
        {code: 'fr', label: this.translateService.instant('language_selector.french')},
        {code: 'pt', label: this.translateService.instant('language_selector.portuguese')},
        {code: 'en', label: this.translateService.instant('language_selector.english')},
    ];

    /**
     * The hardcoded url of the account page in each language.
     * This is necessary to navigate to the account page reloading the page.
     */
    readonly accountUrl: {[lang:string]:string} = {
        'es':  '/es/cuenta',
        'fr':  '/fr/compte',
        'pt':  '/pt/conta',
        'en':  '/en/account',
    }

    private isSideSheetSubject = new BehaviorSubject(false);
    readonly isSideSheet$ = this.isSideSheetSubject.asObservable();

    constructor(
        private localizeRouterService: LocalizeRouterService,
        private localizedRouter: LocalizedRouter,
        private injector: Injector,
        private translateService: TranslateService,
        private router: Router,
    ) {
        try {
            injector.get(SideSheetRef);
            this.isSideSheetSubject.next(true);
            return;
        } catch {}
    }

    private selectedLanguageCodeSubject =  new BehaviorSubject(this.localizeRouterService.parser.currentLang);
    readonly selectedLanguageCode$ = this.selectedLanguageCodeSubject.asObservable();

    selectLanguage(languageCode: string): void {
        this.selectedLanguageCodeSubject.next(languageCode);
    }

    saveSelectionAndExit(): void {
        this.changeLanguage(this.selectedLanguageCodeSubject.value)
    }

    private changeLanguage(languageCode: string): void {
        //Subscribe to the first NavigationEnd event and reload the page.
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                take(1),
            )
            .subscribe({
                next: value => {
                if (this.isSideSheetSubject.value) {
                    //If the language selector is in a side sheet, reloads the page to refetch all the data from the server with the new language.
                    window.location.reload();
                }
            }});
        this.displaySplashScreen();
        if (this.isSideSheetSubject.value) {
            //If the language selector is in a side sheet, changes the language of the current page.
            this.localizeRouterService.changeLanguage(languageCode);
        } else {
            //If the language selector is in a page, we need to navigate to the account page reloading the page.
            // This is necessary to refetch all the data from the server with the new language.
            window.location.href = this.accountUrl[languageCode]
        }
    }

    private displaySplashScreen() {
        SplashScreen.show({
            // Auto hide it's not necessary because the page will be reloaded and the splash screen will be hidden in the app.component.ts.
            // This allows us to show the splash screen during the time it takes to reload the page.
            autoHide: false,
        });
    }
}
